import { useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CountyMultiSelectProps {
  /** Currently selected county slugs (lowercase, e.g. 'pinellas'). */
  value: string[];
  /** Called with the next array of slugs. */
  onChange: (next: string[]) => void;
  /** All counties present in the data set (Plan 05-05 derives from API). */
  availableCounties: string[];
  className?: string;
}

function titleCase(slug: string): string {
  return slug
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}

/**
 * `<CountyMultiSelect>` — multi-select for FL counties (FILT-06).
 *
 * UI-SPEC § 3.9. Closed state shows "All counties" or "{N} selected".
 * Open state: glass popover with one checkbox per available county,
 * alphabetical.
 *
 * Implementation: we use Radix Dialog as a lightweight popover surface
 * (the shadcn install does not include a `popover` primitive but Dialog
 * is already installed and gives us focus management + escape handling).
 * It is positioned with absolute placement under the trigger.
 *
 * Note: this is a controlled component. Plan 05-05 will wire `value` /
 * `onChange` through `useFilters` to URL state.
 */
export function CountyMultiSelect({
  value,
  onChange,
  availableCounties,
  className,
}: CountyMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const sorted = [...availableCounties].sort();

  const toggleCounty = (slug: string, checked: boolean) => {
    if (checked) {
      if (!value.includes(slug)) onChange([...value, slug]);
    } else {
      onChange(value.filter((s) => s !== slug));
    }
  };

  let triggerLabel: string;
  if (value.length === 0) triggerLabel = 'All counties';
  else if (value.length === 1) triggerLabel = titleCase(value[0] ?? '');
  else triggerLabel = `${value.length} selected`;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger
        // Plan 05-06 a11y fix: drop aria-label entirely so the trigger's
        // accessible name comes from its visible text content ("Counties:" +
        // {triggerLabel}). axe label-content-name-mismatch flagged any
        // aria-label that didn't exactly match the concatenated visible text;
        // letting the visible spans BE the name keeps SR + sighted users
        // perfectly aligned. aria-haspopup + aria-expanded still announce
        // the popover semantics.
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'inline-flex items-center gap-2 h-9 px-3 rounded-md border border-foam-50/15 bg-transparent',
          'text-sm font-semibold text-foam-50 hover:bg-foam-50/5',
          'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
          className,
        )}
      >
        <span className="text-foam-50/80">Counties:</span>
        <span>{triggerLabel}</span>
        <ChevronDown aria-hidden className="w-4 h-4 opacity-70" />
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Overlay className="fixed inset-0 z-40 bg-ocean-950/40" />
        <PopoverPrimitive.Content
          className={cn(
            'fixed left-1/2 top-32 z-50 -translate-x-1/2 w-[260px]',
            'bg-ocean-950/95 backdrop-blur-md border border-foam-50/10 rounded-2xl shadow-2xl p-3',
          )}
          aria-label="County filter options"
        >
          <PopoverPrimitive.Title className="text-sm font-semibold text-foam-50 mb-2">
            Filter by county
          </PopoverPrimitive.Title>
          <PopoverPrimitive.Description className="sr-only">
            Toggle counties to include in the dashboard list.
          </PopoverPrimitive.Description>
          <ul className="grid gap-1 max-h-[260px] overflow-y-auto">
            {sorted.length === 0 ? (
              <li className="text-sm text-foam-50/60 px-2 py-1">No counties available.</li>
            ) : (
              sorted.map((slug) => {
                const checked = value.includes(slug);
                return (
                  <li key={slug}>
                    <label className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-ocean-800/60 cursor-pointer">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(c) => toggleCounty(slug, Boolean(c))}
                        aria-label={`Toggle ${titleCase(slug)} county`}
                        className="border-foam-50/40 data-[state=checked]:bg-tide-400 data-[state=checked]:text-ocean-950"
                      />
                      <span className="text-sm text-foam-50">{titleCase(slug)}</span>
                    </label>
                  </li>
                );
              })
            )}
          </ul>
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-xs font-semibold text-foam-50/80 hover:text-foam-50 px-2 py-1"
            >
              Done
            </button>
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
